import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  useFloating,
  useClick,
  useDismiss,
  useListNavigation,
  useTypeahead,
  useInteractions,
  offset,
  flip,
  shift,
  size as floatingSize,
  autoUpdate,
  FloatingPortal,
  FloatingFocusManager,
} from '@floating-ui/react';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowDown01Icon, Cancel01Icon, CheckmarkCircle01Icon } from '@hugeicons/core-free-icons';
import type { LabelPickerProps } from '@overlayqa/shared/ui';

interface SelectOption {
  value: string;
  label: string;
  /**
   * Optional visual shown before the label, in the menu and on the trigger
   * when this option is selected: a glyph or a colour dot, so a choice like
   * a project's Icon or Color is seen rather than imagined (Project Settings,
   * 2026-09-17). Decorative: the label stays the accessible name.
   */
  leading?: ReactNode;
  /**
   * The whole visual for this option, drawn INSTEAD of the label by the `badge`
   * variant: the value's own badge, so a table cell reads exactly as it does
   * when it is not editable. `label` stays the accessible name and the
   * typeahead key, which matters wherever a badge abbreviates ("A11y" for
   * Accessibility). Ignored by every other variant.
   */
  visual?: ReactNode;
}

interface SelectDropdownProps {
  id?: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  /**
   * `input` (default) renders a full form-input trigger (.input, h-8) for
   * filter bars and modals. `ghost` renders a compact bordered text trigger
   * (px-2 py-1) for card headers and settings rows. The two heights are
   * deliberate: input matches the form controls beside it, ghost hugs the
   * section-label line it sits on.
   *
   * `badge` renders no chrome at all: the trigger IS the selected option's own
   * `visual`, for changing a field in place inside a table cell. It expects a
   * `group/row` ancestor (DataTable puts one on every <tr>) so its hint arrow
   * can appear when the pointer is anywhere on the row; without one the arrow
   * still appears on hover or focus of the trigger itself, which is a quieter
   * affordance rather than a broken one.
   */
  variant?: 'input' | 'ghost' | 'badge';
  /**
   * Which input tier the `input` variant renders at, mirroring `.input` vs
   * `.input .input-lg`: `default` is the 36px dense control for filter bars and
   * table rows, `lg` the 40px comfortable one for modals and forms. Without
   * this a modal dropdown stayed 32px while the text fields beside it moved to
   * 40px. `toolbar` matches compact filters: 36px tall, 12px text and a
   * content-sized input trigger bounded to 112–224px. Badge menus use the same
   * typography; their visual supplies the trigger sizing. Ignored by ghost.
   */
  size?: 'default' | 'lg' | 'toolbar';
  /**
   * Renders the trigger inert, matching disabled form fields.
   * `.input` already ships `disabled:opacity-50 disabled:cursor-not-allowed`,
   * so the visual half comes from being a real disabled <button>; `useClick` is
   * switched off too so no code path can open a listbox the user cannot use.
   */
  disabled?: boolean;
  /**
   * Shown in place of the option list when `options` is empty. Without it an
   * empty list opens as a bare ~8px sliver with no text. Callers that know the
   * noun should pass it ("No projects yet") to explain the empty state.
   */
  emptyLabel?: string;
  /** The resting/default value. Active styling only applies when value differs from this. */
  defaultValue?: string;
  /** Toolbar filters only: when set and the value differs from `defaultValue`,
   *  the trigger reads "{name}: {value}" in the blue active look of
   *  MultiSelectFilter, with a clear control that restores the default
   *  (decision 2026-10-11). Sorts omit it. */
  name?: string;
  'aria-label'?: string;
  search?: LabelPickerProps['search'];
  onOpenChange?: (open: boolean) => void;
}

export function SelectDropdown({
  id: suppliedId,
  value,
  options,
  onChange,
  placeholder = 'Select...',
  className = '',
  variant = 'input',
  size = 'default',
  defaultValue = '',
  disabled = false,
  emptyLabel = 'No options',
  name,
  'aria-label': ariaLabel,
  search: searchField,
  onOpenChange,
}: SelectDropdownProps) {
  const generatedId = useId();
  const id = suppliedId ?? generatedId;
  const [isOpen, setIsOpen] = useState(false);
  const openChangeRef = useRef(onOpenChange);
  openChangeRef.current = onOpenChange;
  useEffect(() => { openChangeRef.current?.(isOpen); }, [isOpen]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const searchInput = useRef<HTMLInputElement>(null);

  // useListNavigation moves real DOM focus between these nodes, so the option
  // elements must be registered in source order. useTypeahead matches against
  // the labels to support jump-to-letter.
  const listRef = useRef<Array<HTMLElement | null>>([]);
  const labelsRef = useRef<Array<string | null>>([]);
  labelsRef.current = options.map((o) => o.label);

  const foundIndex = options.findIndex((o) => o.value === value);
  const selectedIndex = foundIndex === -1 ? null : foundIndex;

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: open => { setIsOpen(open); if (!open) searchField?.onChange(''); },
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [offset(4), flip(), shift({ padding: 8 }), floatingSize({
      padding: 8,
      apply({ availableHeight, availableWidth, rects, elements }) {
        Object.assign(elements.floating.style, {
          minWidth: `${Math.min(rects.reference.width, availableWidth)}px`,
          maxWidth: `${Math.max(0, availableWidth)}px`,
          maxHeight: `${Math.max(0, Math.min(240, availableHeight))}px`,
        });
      },
    })],
    whileElementsMounted: autoUpdate,
  });

  // Disabled means disabled at the interaction layer too, not just visually.
  const click = useClick(context, { enabled: !disabled });
  const dismiss = useDismiss(context);
  const listNavigation = useListNavigation(context, {
    enabled: !searchField,
    listRef,
    activeIndex,
    selectedIndex,
    onNavigate: setActiveIndex,
    loop: true,
  });
  const typeahead = useTypeahead(context, {
    enabled: !searchField,
    listRef: labelsRef,
    activeIndex,
    selectedIndex,
    onMatch: setActiveIndex,
  });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    click,
    dismiss,
    listNavigation,
    typeahead,
  ]);

  /**
   * Going disabled must CLOSE the panel, not just hide it. Rendering behind
   * `isOpen && !disabled` alone leaves `isOpen` true, so re-enabling pops the
   * panel back open over whatever the user moved on to. That is reachable
   * wherever one mutation disables several controls at once, which is exactly
   * ProjectSharingSection: opening one row's dropdown while another row saves
   * would hide it, and settling the save would reopen it.
   *
   * Idempotent, so React.StrictMode's dev-only double-invoke is harmless.
   */
  useEffect(() => {
    if (disabled && isOpen) {
      setIsOpen(false);
      setActiveIndex(null);
    }
  }, [disabled, isOpen]);

  const selectedOption = options.find((o) => o.value === value);
  const selectedLabel = selectedOption?.label ?? placeholder;
  const isBadge = variant === 'badge';
  const isToolbar = variant !== 'ghost' && size === 'toolbar';
  const namedFilterActive = isToolbar && !!name && value !== defaultValue;

  function selectOption(optionValue: string) {
    onChange(optionValue);
    searchField?.onChange('');
    (refs.domReference.current as HTMLElement | null)?.focus();
    setIsOpen(false);
    setActiveIndex(null);
  }

  // Keep the menu in the modal's focus boundary and the native dialog top layer.
  const dialog = refs.domReference.current?.closest<HTMLElement>('dialog, [role="dialog"]');

  function handleOpenKeyDown(event: KeyboardEvent) {
    if (searchField && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault(); event.stopPropagation();
      if (!isOpen) { setIsOpen(true); return; }
      if (options.length) {
        const index = activeIndex === null ? (event.key === 'ArrowDown' ? 0 : options.length - 1)
          : (activeIndex + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
        setActiveIndex(index); listRef.current[index]?.focus();
      }
      return;
    }
    if (!isOpen) return;
    if (searchField && event.target === searchInput.current && !['Escape', 'Tab'].includes(event.key)) {
      event.stopPropagation(); return;
    }
    if (searchField && ['Home', 'End'].includes(event.key)) {
      event.preventDefault(); event.stopPropagation();
      const index = event.key === 'Home' ? 0 : options.length - 1;
      setActiveIndex(index); listRef.current[index]?.focus(); return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setIsOpen(false);
      searchField?.onChange('');
      (refs.domReference.current as HTMLElement | null)?.focus();
    } else if (event.key === 'Tab' && dialog) {
      event.preventDefault();
      const controls = Array.from(dialog.querySelectorAll<HTMLElement>(
        'button:not(:disabled), input:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]',
      )).filter(node => !node.closest('[data-select-menu], [data-floating-ui-portal]')
        && !node.hidden && node.getAttribute('aria-hidden') !== 'true'
        && (typeof node.checkVisibility !== 'function' || node.checkVisibility()));
      const index = controls.indexOf(refs.domReference.current as HTMLElement);
      const next = controls[(index + (event.shiftKey ? -1 : 1) + controls.length) % controls.length];
      setIsOpen(false);
      searchField?.onChange('');
      next?.focus();
    }
  }

  return (
    <>
      <span className={namedFilterActive ? 'inline-flex w-max min-w-0 max-w-56' : 'contents'}>
      <button
        id={id}
        value={value}
        ref={refs.setReference}
        {...getReferenceProps({ onKeyDown: handleOpenKeyDown })}
        type="button"
        title={isToolbar ? (namedFilterActive ? `${name}: ${selectedLabel}` : selectedLabel) : undefined}
        className={
          isBadge
            // No chrome of its own: the badge supplies the box. `-m-1 p-1` grows the
            // hit area by 4px on every side WITHOUT moving the badge, so an editable
            // cell and a read-only one are pixel-identical at rest. The hover backing
            // is the row-actions button's (RowActionMenu.tsx:32); the ring and press
            // are `.btn`'s (index.css:564), so this control borrows rather than invents.
            ? `group/edit relative inline-flex items-center rounded-md -m-1 p-1 transition-[color,background-color,transform] hover:bg-gray-100 aria-expanded:bg-gray-100 active:scale-[0.97] motion-reduce:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${className}`
            : variant === 'ghost'
              ? `flex items-center justify-center gap-1 text-xs font-normal text-gray-700 hover:text-gray-900 border border-gray-200 rounded-md px-2 py-1 transition-colors ${className}`
              // Match the text fields beside this control. Ghost controls keep
              // the smaller type used by compact card headers and settings rows.
              : `input${size === 'lg' ? ' input-lg' : isToolbar ? (namedFilterActive ? ' input-toolbar min-w-0 flex-1 rounded-r-none border-r-0 border-primary-500 bg-primary-50' : ' input-toolbar w-auto min-w-28 max-w-56') : ''} flex items-center justify-between gap-2 text-left ${className}`
        }
        disabled={disabled}
        aria-haspopup={searchField ? 'dialog' : 'listbox'}
        aria-expanded={isOpen}
        /* The badge trigger's own text is the VALUE ("Open"), so an aria-label of
           just the field would drop the value and the value alone would drop the
           field. Both, or a screen reader cannot tell what this button changes. */
        aria-label={isBadge && ariaLabel ? `${ariaLabel}: ${selectedLabel}` : ariaLabel}
        aria-controls={isOpen ? `${id}-${searchField ? 'menu' : 'options'}` : undefined}
      >
        {/* `truncate` + `min-w-0` because the trigger has a FIXED height: a
            label too long for its width must clip, never reflow to a second
            line and render taller than the filters beside it. Moving the
            trigger from 12px to 14px made "Last Updated" do exactly that
            inside a w-32 control (caught in a journey screenshot 2026-08-07).
            The call sites were widened to fit too; this is the guarantee that
            holds for the next long label. */}
        {isBadge ? (
          selectedOption?.visual ?? <span className="truncate min-w-0">{selectedLabel}</span>
        ) : (() => {
          const label = (
            <span className={`truncate min-w-0 font-normal ${variant === 'ghost' ? '' : namedFilterActive ? 'text-primary-700' : value !== defaultValue ? 'text-gray-900' : isToolbar ? 'text-gray-700' : 'text-gray-400'}`}>
              {namedFilterActive ? `${name}: ${selectedLabel}` : selectedLabel}
            </span>
          );
          // Only an option that carries a leading visual changes the trigger's
          // DOM, so every text-only caller renders exactly as before.
          if (!selectedOption?.leading) return label;
          return (
            <span className="flex items-center gap-2 min-w-0">
              <span className="flex shrink-0" aria-hidden="true">{selectedOption.leading}</span>
              {label}
            </span>
          );
        })()}
        {isBadge ? (
          /* Out of flow and 1px past the badge's right edge, so an editable cell
             measures the same as a read-only one and no column widens. Hidden at
             rest: a list of rows must look untouched until a pointer or the
             keyboard arrives. `pointer-events-none` keeps the click on the button
             underneath, which is what makes the whole badge one target. */
          <span
            data-hint-arrow=""
            aria-hidden="true"
            className="pointer-events-none absolute left-full top-1/2 -translate-y-1/2 -ml-px flex w-3 h-3 text-gray-400 opacity-0 transition-[opacity,transform,color] group-hover/row:opacity-100 group-hover/edit:opacity-100 group-hover/edit:text-gray-600 group-focus-visible/edit:opacity-100 group-focus-visible/edit:text-gray-600 group-aria-expanded/edit:opacity-100 group-aria-expanded/edit:text-gray-600 group-aria-expanded/edit:rotate-180"
          >
            <HugeiconsIcon icon={ArrowDown01Icon} size={12} strokeWidth={2} color="currentColor" className="w-3 h-3" aria-hidden="true" />
          </span>
        ) : (
          <HugeiconsIcon
            icon={ArrowDown01Icon}
            size={12}
            strokeWidth={2}
            color="currentColor"
            className={`w-3 h-3 ${isToolbar ? 'text-gray-600' : 'text-gray-400'} flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        )}
      </button>
      {namedFilterActive && <button type="button" disabled={disabled} aria-label={`Clear ${name!.toLowerCase()} filter`}
        className="input input-toolbar flex w-[var(--oq-control-app-toolbar-height)] shrink-0 items-center justify-center rounded-l-none border-l-0 border-primary-500 bg-primary-50 px-0 text-primary-700 hover:bg-primary-100"
        onClick={() => onChange(defaultValue)}>
        <HugeiconsIcon icon={Cancel01Icon} size={12} strokeWidth={2} className="h-3 w-3" aria-hidden="true" />
      </button>}
      </span>

      {isOpen && !disabled && (
        <FloatingPortal root={dialog ?? undefined} preserveTabOrder={!dialog}>
          {/* modal={false} keeps the rest of the page reachable (this is a
              listbox, not a dialog); the manager still returns focus to the
              trigger on close so Escape does not strand the user. */}
          <FloatingFocusManager context={context} modal={false} returnFocus={!dialog} initialFocus={searchField ? searchInput : 0}>
            <div
              ref={refs.setFloating}
              id={`${id}-menu`}
              aria-labelledby={id}
              data-select-menu=""
              style={floatingStyles}
              {...getFloatingProps({ onKeyDown: handleOpenKeyDown })}
              className="bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[10rem] max-h-60 overflow-y-auto"
              role={searchField ? 'dialog' : undefined}
            >
              {searchField && <div className="px-2 pb-1">
                <input ref={searchInput} type="search" className={`input w-full${isToolbar ? ' input-toolbar' : ''}`} aria-label="Search tags" placeholder={searchField.placeholder} value={searchField.value} maxLength={searchField.maxLength}
                  onChange={event => { searchField.onChange(event.target.value); setActiveIndex(null); }} />
              </div>}
              <ul id={`${id}-options`} role="listbox" aria-labelledby={id}>
              {/* An empty list must say so. `role="presentation"` keeps it out of
                  the option set, so arrow keys and typeahead cannot land on it
                  and a screen reader does not offer it as a choice. text-gray-500
                  (#6b7280, 4.83:1 on white) rather than the gray-400 used for the
                  chevron, which is 3.08:1 and under the 4.5:1 floor for text. */}
              {options.length === 0 && (
                <li role="presentation" className={`px-3 py-2 text-gray-500 oq-text-body`}>
                  {searchField ? 'No matching tags' : emptyLabel}
                </li>
              )}
              {options.map((option, index) => (
                <li
                  key={option.value}
                  ref={(node) => {
                    listRef.current[index] = node;
                  }}
                  role="option"
                  data-value={option.value}
                  aria-selected={option.value === value}
                  /* A badge may abbreviate what the label spells out ("A11y" for
                     Accessibility), so the name comes from the label, never the
                     rendered visual. Only the badge variant needs this. */
                  aria-label={isBadge ? option.label : undefined}
                  // Roving tabindex: the active option is the one tab stop, so
                  // the list is reachable and arrow keys move within it.
                  tabIndex={activeIndex === index ? 0 : -1}
                  // Compact menus keep the same padding and line height as
                  // other pickers; only the font size changes.
                  className={`px-3 py-2 oq-text-body cursor-pointer transition-colors outline-none ${
                    /* The trailing check marks selection; tint is only hover or
                       keyboard focus (decision 2026-10-11). */
                    `${index % 2 === 1 ? 'bg-gray-50/60' : ''} text-gray-700 hover:bg-gray-100`
                  } ${activeIndex === index ? 'bg-gray-100' : ''}`}
                  {...getItemProps({
                    onClick: () => selectOption(option.value),
                    onKeyDown: (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        selectOption(option.value);
                      }
                    },
                  })}
                >
                  {/* All variants mark the current value without increasing text
                      weight. Reserve the same trailing space on every option. */}
                  <span className="flex items-center justify-between gap-3">
                    <span className="flex min-w-0 items-center gap-2">
                      {isBadge ? option.visual ?? option.label : <>
                        {option.leading && <span className="flex shrink-0" aria-hidden="true">{option.leading}</span>}
                        <span className="min-w-0">{option.label}</span>
                      </>}
                    </span>
                    {option.value === value ? (
                      <span data-current-mark="" aria-hidden="true" className="flex shrink-0 text-primary-600">
                        <HugeiconsIcon icon={CheckmarkCircle01Icon} size={14} strokeWidth={2} color="currentColor" className="w-3.5 h-3.5" aria-hidden="true" />
                      </span>
                    ) : (
                      <span className="w-3.5 shrink-0" aria-hidden="true" />
                    )}
                  </span>
                </li>
              ))}
              </ul>
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}
